import React, { useState, useMemo, memo } from "react";
import { Paper, Grid, Typography, IconButton } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { delay } from "lodash";
import {
  sortTypeOptions,
  pogSortByOptions,
  pogFilterByOptions,
} from "../../../utils";
import useStableCallback from "../../../hooks/useStableCallback";
import { setPogFilters, resetPogFilters } from "../../../store/globalSlice";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomDateRangePicker from "../../../components/CustomDateRangePicker";

const INPUT_REQUIRED_FILTER_INDEX = ["0", "1", "2"];

const RELEASE_DATE_FILTER_INDEX = "14";

const PaperSx = { pl: 1 };

const CenterAlignSx = { display: "flex", alignItems: "center" };

const FlexEndSx = { ...CenterAlignSx, justifyContent: "flex-end" };

const CustomDropdownSx = { fontSize: "10px", mx: 1 };

const PogFilter = ({
  disableFilters = false,
  handleFilter,
  handleReset,
  handleSortChange,
}) => {
  const dispatch = useDispatch();
  const { filterBy, filterValue, startDate, endDate, sortBy, sortType } =
    useSelector((store) => store.global.pogFilters);
  const [releaseDateError, setReleaseDateError] = useState(false);

  const handleFilterBy = useStableCallback((e) => {
    dispatch(
      setPogFilters({
        field: "filterBy",
        value: e?.target?.value,
      })
    );
    dispatch(
      setPogFilters({
        field: "filterValue",
        value: "",
      })
    );
  });

  const handleFilterValue = useStableCallback((e) => {
    dispatch(setPogFilters({ field: "filterValue", value: e?.target?.value }));
  });

  const handleSortBy = useStableCallback((e) => {
    dispatch(setPogFilters({ field: "sortBy", value: e?.target?.value }));
    // throttle for sorting
    delay(handleSortChange);
  });

  const handleSortType = useStableCallback((e) => {
    dispatch(setPogFilters({ field: "sortType", value: e?.target?.value }));
    // throttle for sorting
    delay(handleSortChange);
  });

  const handleFilterApply = useStableCallback((e) => {
    handleFilter();
  });

  const handleFilterReset = useStableCallback((e) => {
    setReleaseDateError(false);
    dispatch(resetPogFilters());
    delay(handleReset);
  });

  const checkDateError = useStableCallback((error) => {
    setReleaseDateError(error);
  });

  const disableSearchAction = useMemo(
    () =>
      disableFilters ||
      !filterBy ||
      (INPUT_REQUIRED_FILTER_INDEX.includes(filterBy) &&
        !filterValue?.trim()?.length) ||
      (RELEASE_DATE_FILTER_INDEX === filterBy && (!startDate || !endDate)) ||
      !!releaseDateError,
    [
      disableFilters,
      filterBy,
      filterValue,
      startDate,
      endDate,
      releaseDateError,
    ]
  );

  return (
    <Paper sx={{ ...PaperSx, ...(!!releaseDateError && { pb: 3.5 }) }}>
      <Grid container spacing={0.5}>
        <Grid item xs={7.7} sx={CenterAlignSx}>
          <Typography variant="caption" fontWeight={500} lineHeight="unset">
            Search POG By:
          </Typography>
          <CustomDropdown
            inputsx={CustomDropdownSx}
            value={filterBy}
            onChange={handleFilterBy}
            disabled={disableFilters}
          >
            <option aria-label="None" value="">
              None
            </option>
            {Object.keys(pogFilterByOptions).map((optionIndex) => (
              <option key={optionIndex} value={optionIndex}>
                {pogFilterByOptions[optionIndex].label}
              </option>
            ))}
          </CustomDropdown>
          {INPUT_REQUIRED_FILTER_INDEX.includes(filterBy) && (
            <CustomInput
              value={filterValue}
              onChange={handleFilterValue}
              disabled={disableFilters}
              inputProps={{
                maxlength: 250,
              }}
              sx={{ mr: 1 }}
            />
          )}
          {RELEASE_DATE_FILTER_INDEX === filterBy && (
            <CustomDateRangePicker isErrorPresent={checkDateError} />
          )}
          <IconButton
            aria-label="search"
            color="primary"
            size="small"
            onClick={handleFilterApply}
            disabled={disableSearchAction}
            sx={{ px: 0, pr: 1 }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="reset"
            color="primary"
            size="small"
            onClick={handleFilterReset}
            disabled={disableFilters}
            sx={{ px: 0 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Grid item xs={4.3} sx={FlexEndSx}>
          <Typography variant="caption" fontWeight={500} lineHeight="unset">
            Sort By:
          </Typography>
          <CustomDropdown
            inputsx={CustomDropdownSx}
            value={sortBy}
            onChange={handleSortBy}
            disabled={disableFilters}
          >
            {Object.keys(pogSortByOptions).map((optionIndex) => (
              <option key={optionIndex} value={optionIndex}>
                {pogSortByOptions[optionIndex].label}
              </option>
            ))}
          </CustomDropdown>
          <Typography
            variant="caption"
            fontWeight={500}
            lineHeight="unset"
            sx={{ ml: 1 }}
          >
            Sort Type:
          </Typography>
          <CustomDropdown
            inputsx={CustomDropdownSx}
            value={sortType}
            onChange={handleSortType}
            disabled={disableFilters}
          >
            {sortTypeOptions?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </CustomDropdown>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default memo(PogFilter);
